import type { ComputedRef, Ref } from 'vue'

import type { ConsistencyAssetCollection, StoryboardPreviewOverlayState, StoryboardPromptReferencePayload, StoryboardSceneDraft } from './types'

export type SceneImageAction = 'generate' | 'select' | 'clear' | 'upload'

const SCENE_IMAGE_SOURCE_LABELS: Record<string, string> = {
  generated: 'AI 生成',
  generated_material: 'AI 生成并归档',
  manual: '手动选择',
  manual_url: '外部链接',
  uploaded: '本地上传',
}

interface KnowledgeSceneImageSelection {
  sceneNumber?: number | null
  image_url?: string
  title?: string
}

interface KnowledgeConsistencyAssetImageSelection {
  assetType: keyof ConsistencyAssetCollection
  assetId: string
  image_url: string
  material_id?: number
  title?: string
}

interface UploadedMaterial {
  id?: number
  name?: string
  file_url?: string
}

interface SceneImageWorkspaceOptions {
  projectId: number
  workflow: Ref<any>
  storyboardScenes: ComputedRef<StoryboardSceneDraft[]>
  consistencyAssets: Ref<ConsistencyAssetCollection>
  consistencyDirty: Ref<boolean>
  sceneImageLoading: Ref<Record<number, SceneImageAction | null>>
  sceneImageInputs: Ref<Record<number, string>>
  sceneUploadInput: Ref<HTMLInputElement | null>
  activeUploadSceneNumber: Ref<number | null>
  ensureStoryboardScenesReady: () => Promise<boolean>
  ensureConsistencyAssetsReady: () => Promise<boolean>
  buildSceneGenerationRequest: (sceneNumber: number, purpose: 'image' | 'video') => StoryboardPromptReferencePayload
  applyStoryboardSceneUpdateFromServer: (scene: Record<string, any> | null | undefined) => void
  handleSaveConsistencyAssets: (successMessage?: string) => Promise<void>
  generateSceneImageApi: (projectId: number, sceneNumber: number, payload?: StoryboardPromptReferencePayload & { image_model_id?: string }) => Promise<{ workflow: any; scene?: Record<string, any> | null }>
  selectSceneImageApi: (projectId: number, sceneNumber: number, payload: { image_url: string; source: string }) => Promise<{ workflow: any; scene?: Record<string, any> | null }>
  clearSceneImageApi: (projectId: number, sceneNumber: number) => Promise<{ workflow: any; scene?: Record<string, any> | null }>
  uploadMaterial: (formData: FormData, options: { project_id: number; material_type: string; description: string }) => Promise<UploadedMaterial>
  consumeQueuedKnowledgeSceneImages: (projectId: number) => KnowledgeSceneImageSelection[]
  consumeQueuedKnowledgeConsistencyAssetImages: (projectId: number) => KnowledgeConsistencyAssetImageSelection[]
  success: (message: string) => void
  error: (message: string) => void
  confirmClearSceneImage: (sceneNumber: number) => Promise<boolean>
}

function cloneValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

export function useSceneImageWorkspace(options: SceneImageWorkspaceOptions) {
  function setSceneImageLoading(sceneNumber: number, action: SceneImageAction | null) {
    options.sceneImageLoading.value = { ...options.sceneImageLoading.value, [sceneNumber]: action }
  }

  function isSceneImageLoading(sceneNumber: number, action?: SceneImageAction) {
    const currentAction = options.sceneImageLoading.value[sceneNumber] || null
    return action ? currentAction === action : Boolean(currentAction)
  }

  function isSceneImageBusy(sceneNumber: number) {
    return isSceneImageLoading(sceneNumber)
  }

  function buildSceneImageOverlayState(action: SceneImageAction | null): StoryboardPreviewOverlayState | null {
    if (action === 'generate') {
      return {
        title: '图片生成中',
        description: '正在结合当前分镜描述、创意补充和一致性资产生成镜头图片。',
        note: '生成完成后会自动回填当前镜头。',
      }
    }
    if (action === 'select') {
      return {
        title: '参考图应用中',
        description: '正在把选中的参考图绑定到当前镜头，并同步更新分镜草稿。',
      }
    }
    if (action === 'upload') {
      return {
        title: '图片上传中',
        description: '正在上传图片并回填到当前镜头。',
      }
    }
    if (action === 'clear') {
      return {
        title: '正在移除当前图片',
        description: '系统正在清除当前镜头已选图片，并恢复为空状态。',
      }
    }
    return null
  }

  async function handleGenerateSceneImage(sceneNumber: number, imageModelId?: string) {
    const ready = await options.ensureStoryboardScenesReady()
    if (!ready) {
      return
    }

    const consistencyReady = await options.ensureConsistencyAssetsReady()
    if (!consistencyReady) {
      return
    }

    setSceneImageLoading(sceneNumber, 'generate')
    try {
      const requestPayload = options.buildSceneGenerationRequest(sceneNumber, 'image')
      const response = await options.generateSceneImageApi(
        options.projectId,
        sceneNumber,
        requestPayload.prompt || requestPayload.referenced_assets || imageModelId
          ? { ...requestPayload, image_model_id: imageModelId }
          : undefined,
      )
      options.workflow.value = response.workflow
      options.applyStoryboardSceneUpdateFromServer(response.scene)
      options.sceneImageInputs.value = { ...options.sceneImageInputs.value, [sceneNumber]: String(response.scene?.background_image_url || '') }
      options.success(`场景 ${sceneNumber} 图片已生成`)
    } catch (error: any) {
      options.error(error?.response?.data?.detail || error?.response?.data?.message || '分镜图片生成失败')
    } finally {
      setSceneImageLoading(sceneNumber, null)
    }
  }

  async function handleSelectSceneReference(sceneNumber: number, asset: { title: string; image_url: string }) {
    const ready = await options.ensureStoryboardScenesReady()
    if (!ready) {
      return
    }

    setSceneImageLoading(sceneNumber, 'select')
    try {
      const response = await options.selectSceneImageApi(options.projectId, sceneNumber, {
        image_url: asset.image_url,
        source: asset.title,
      })
      options.workflow.value = response.workflow
      options.applyStoryboardSceneUpdateFromServer(response.scene)
      options.sceneImageInputs.value = { ...options.sceneImageInputs.value, [sceneNumber]: asset.image_url }
      options.success(`场景 ${sceneNumber} 已选用参考图`)
    } catch (error: any) {
      options.error(error?.response?.data?.detail || error?.response?.data?.message || '选用参考图失败')
    } finally {
      setSceneImageLoading(sceneNumber, null)
    }
  }

  async function applyQueuedKnowledgeSceneImages() {
    const queuedSelections = options.consumeQueuedKnowledgeSceneImages(options.projectId)
    if (!queuedSelections.length) {
      return
    }

    for (const selection of queuedSelections) {
      if (!selection.sceneNumber || !selection.image_url) {
        continue
      }

      try {
        const response = await options.selectSceneImageApi(options.projectId, selection.sceneNumber, {
          image_url: selection.image_url,
          source: `knowledge:${selection.title}`,
        })
        options.workflow.value = response.workflow
        options.sceneImageInputs.value = { ...options.sceneImageInputs.value, [selection.sceneNumber]: selection.image_url }
      } catch (error) {
        console.error('Failed to apply queued knowledge scene image', error)
      }
    }

    options.success('已从素材中心为当前分镜带回参考图')
  }

  async function applyQueuedKnowledgeConsistencyAssetImages() {
    const queuedSelections = options.consumeQueuedKnowledgeConsistencyAssetImages(options.projectId)
    if (!queuedSelections.length) {
      return
    }

    const nextAssets: ConsistencyAssetCollection = {
      character: cloneValue(options.consistencyAssets.value.character),
      scene: cloneValue(options.consistencyAssets.value.scene),
      prop: cloneValue(options.consistencyAssets.value.prop),
    }
    let changed = false

    queuedSelections.forEach((selection) => {
      const list = nextAssets[selection.assetType]
      const assetIndex = list.findIndex((item) => item.id === selection.assetId)
      if (assetIndex < 0) {
        return
      }

      list[assetIndex] = {
        ...list[assetIndex],
        image_url: selection.image_url,
        material_id: selection.material_id,
        selected_image_source: `knowledge:${selection.title}`,
        generated_prompt: '',
      }
      changed = true
    })

    if (!changed) {
      return
    }

    options.consistencyAssets.value = nextAssets
    options.consistencyDirty.value = true
    await options.handleSaveConsistencyAssets('已从素材中心带回一致性资产参考图')
  }

  function formatSceneImageSource(source: string) {
    if (!source) {
      return '未标注'
    }

    if (source.startsWith('uploaded_file:')) {
      const fileName = source.slice('uploaded_file:'.length).trim()
      return fileName ? `本地上传 · ${fileName}` : '本地上传'
    }

    if (source.startsWith('knowledge:')) {
      const title = source.slice('knowledge:'.length).trim()
      return title ? `素材参考 · ${title}` : '素材参考'
    }

    return SCENE_IMAGE_SOURCE_LABELS[source] || source
  }

  async function handleClearSceneImage(sceneNumber: number) {
    const scene = options.storyboardScenes.value.find((item) => item.scene_number === sceneNumber)
    if (!scene?.background_image_url) {
      return
    }

    const ready = await options.ensureStoryboardScenesReady()
    if (!ready) {
      return
    }

    const confirmed = await options.confirmClearSceneImage(sceneNumber)
    if (!confirmed) {
      return
    }

    setSceneImageLoading(sceneNumber, 'clear')
    try {
      const response = await options.clearSceneImageApi(options.projectId, sceneNumber)
      options.workflow.value = response.workflow
      options.applyStoryboardSceneUpdateFromServer(response.scene)
      options.sceneImageInputs.value = { ...options.sceneImageInputs.value, [sceneNumber]: '' }
      options.success(`场景 ${sceneNumber} 当前图片已删除`)
    } catch (error: any) {
      options.error(error?.response?.data?.detail || error?.response?.data?.message || '删除当前图片失败')
    } finally {
      setSceneImageLoading(sceneNumber, null)
    }
  }

  function triggerSceneFileUpload(sceneNumber: number) {
    if (isSceneImageBusy(sceneNumber)) {
      return
    }

    options.activeUploadSceneNumber.value = sceneNumber
    if (options.sceneUploadInput.value) {
      options.sceneUploadInput.value.value = ''
      options.sceneUploadInput.value.click()
    }
  }

  async function handleSceneFileSelected(event: Event) {
    const target = event.target as HTMLInputElement | null
    const file = target?.files?.[0]
    const sceneNumber = options.activeUploadSceneNumber.value

    if (!file || sceneNumber === null) {
      return
    }

    const ready = await options.ensureStoryboardScenesReady()
    if (!ready) {
      return
    }

    setSceneImageLoading(sceneNumber, 'upload')
    try {
      const formData = new FormData()
      formData.append('file', file)

      const material = await options.uploadMaterial(formData, {
        project_id: options.projectId,
        material_type: 'background_image',
        description: `storyboard-scene-${sceneNumber}`,
      })

      const imageUrl = String(material.file_url || '').trim()
      if (!imageUrl) {
        throw new Error('上传成功，但未返回图片地址')
      }

      const response = await options.selectSceneImageApi(options.projectId, sceneNumber, {
        image_url: imageUrl,
        source: `uploaded_file:${material.name}`,
      })
      options.workflow.value = response.workflow
      options.applyStoryboardSceneUpdateFromServer(response.scene)
      options.sceneImageInputs.value = { ...options.sceneImageInputs.value, [sceneNumber]: imageUrl }
      options.success(`场景 ${sceneNumber} 图片已上传并选用`)
    } catch (error: any) {
      options.error(error?.response?.data?.detail || error?.response?.data?.message || error?.message || '上传图片失败')
    } finally {
      setSceneImageLoading(sceneNumber, null)
      options.activeUploadSceneNumber.value = null
      if (target) {
        target.value = ''
      }
    }
  }

  return {
    applyQueuedKnowledgeConsistencyAssetImages,
    applyQueuedKnowledgeSceneImages,
    buildSceneImageOverlayState,
    formatSceneImageSource,
    handleClearSceneImage,
    handleGenerateSceneImage,
    handleSceneFileSelected,
    handleSelectSceneReference,
    isSceneImageBusy,
    isSceneImageLoading,
    setSceneImageLoading,
    triggerSceneFileUpload,
  }
}